/**
 * TaskBoardWidget — Project task/ticket tracker (kanban-style)
 * Reads from Supabase `tasks` table. Authenticated users can create/update.
 * CSS prefix: tbw-
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase, isHtmlError, SUPABASE_DOWN_MESSAGE } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import {
  ClipboardList, Plus, ChevronDown, ChevronRight, Filter,
  AlertCircle, Clock, CheckCircle2, CircleDot, Ban, Loader,
  X, Save, Trash2, Edit3
} from 'lucide-react'
import './TaskBoardWidget.css'

const STATUSES = [
  { id: 'backlog', label: 'Backlog', icon: ClipboardList, color: '#666' },
  { id: 'todo', label: 'To Do', icon: CircleDot, color: '#00B8D4' },
  { id: 'in_progress', label: 'In Progress', icon: Loader, color: '#FFB300' },
  { id: 'review', label: 'Review', icon: Clock, color: '#AB47BC' },
  { id: 'done', label: 'Done', icon: CheckCircle2, color: '#00C853' },
  { id: 'blocked', label: 'Blocked', icon: Ban, color: '#ff4444' },
]

const PRIORITIES = [
  { id: 'p0', label: 'P0 Critical', color: '#ff4444', emoji: '🔴' },
  { id: 'p1', label: 'P1 High', color: '#FFB300', emoji: '🟡' },
  { id: 'p2', label: 'P2 Medium', color: '#00B8D4', emoji: '🔵' },
  { id: 'p3', label: 'P3 Low', color: '#666', emoji: '⚪' },
]

const CATEGORIES = [
  { id: 'scraper', label: 'Scraper', color: '#4CAF50' },
  { id: 'widget', label: 'Widget', color: '#2196F3' },
  { id: 'infra', label: 'Infrastructure', color: '#FF9800' },
  { id: 'model', label: 'Model/ML', color: '#9C27B0' },
  { id: 'auth', label: 'Auth/Billing', color: '#F44336' },
  { id: 'mobile', label: 'Mobile', color: '#00BCD4' },
  { id: 'analytics', label: 'Analytics', color: '#E91E63' },
  { id: 'general', label: 'General', color: '#607D8B' },
]

const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.id, s]))
const PRIORITY_MAP = Object.fromEntries(PRIORITIES.map(p => [p.id, p]))
const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.id, c]))

export default function TaskBoardWidget({ title = 'Project Tasks', countLabel = 'tasks' } = {}) {
  const { isAuthenticated, user } = useAuth()
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [viewMode, setViewMode] = useState('list') // 'list' or 'board'
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterPriority, setFilterPriority] = useState('all')
  const [filterCategory, setFilterCategory] = useState('all')
  const [sortBy, setSortBy] = useState('priority') // 'priority', 'created', 'updated', 'status'
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingTask, setEditingTask] = useState(null)
  const [collapsedStatuses, setCollapsedStatuses] = useState(new Set(['done']))

  const fetchTasks = useCallback(async () => {
    if (!supabase) {
      setError('Supabase not configured')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchErr } = await supabase
        .from('tasks')
        .select('*')
        .order('created_at', { ascending: false })

      if (fetchErr) throw fetchErr
      setTasks(data || [])
    } catch (e) {
      const msg = e.message || 'Failed to load tasks'
      setError(isHtmlError(msg) ? SUPABASE_DOWN_MESSAGE : msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchTasks() }, [fetchTasks])

  // Filter and sort
  const filtered = tasks.filter(t => {
    if (filterStatus !== 'all' && t.status !== filterStatus) return false
    if (filterPriority !== 'all' && t.priority !== filterPriority) return false
    if (filterCategory !== 'all' && t.category !== filterCategory) return false
    return true
  })

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'priority') {
      const pa = PRIORITIES.findIndex(p => p.id === a.priority)
      const pb = PRIORITIES.findIndex(p => p.id === b.priority)
      if (pa !== pb) return pa - pb
      return new Date(b.created_at) - new Date(a.created_at)
    }
    if (sortBy === 'status') {
      const sa = STATUSES.findIndex(s => s.id === a.status)
      const sb = STATUSES.findIndex(s => s.id === b.status)
      if (sa !== sb) return sa - sb
      return new Date(b.created_at) - new Date(a.created_at)
    }
    if (sortBy === 'updated') return new Date(b.updated_at) - new Date(a.updated_at)
    return new Date(b.created_at) - new Date(a.created_at)
  })

  // Group by status for board view
  const grouped = {}
  for (const s of STATUSES) grouped[s.id] = []
  for (const t of sorted) {
    if (grouped[t.status]) grouped[t.status].push(t)
  }

  // Stats
  const stats = {
    total: tasks.length,
    done: tasks.filter(t => t.status === 'done').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    blocked: tasks.filter(t => t.status === 'blocked').length,
    p0: tasks.filter(t => t.priority === 'p0' && t.status !== 'done').length,
  }

  const updateTaskStatus = async (taskId, newStatus) => {
    if (!isAuthenticated) return
    const { error: err } = await supabase
      .from('tasks')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', taskId)
    if (!err) {
      setTasks(prev => prev.map(t =>
        t.id === taskId ? { ...t, status: newStatus, updated_at: new Date().toISOString() } : t
      ))
    }
  }

  const deleteTask = async (taskId) => {
    if (!isAuthenticated) return
    const { error: err } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId)
    if (!err) {
      setTasks(prev => prev.filter(t => t.id !== taskId))
    }
  }

  const toggleCollapse = (statusId) => {
    setCollapsedStatuses(prev => {
      const next = new Set(prev)
      if (next.has(statusId)) next.delete(statusId)
      else next.add(statusId)
      return next
    })
  }

  return (
    <div className="widget tbw-widget">
      {/* Header */}
      <div className="widget-header">
        <div className="widget-header-left">
          <ClipboardList size={16} />
          <h3 className="widget-title">{title}</h3>
          <span className="tbw-count">{stats.total} {countLabel}</span>
        </div>
        <div className="widget-header-right">
          {isAuthenticated && (
            <button className="tbw-add-btn" onClick={() => setShowCreateForm(!showCreateForm)}>
              <Plus size={14} /> New
            </button>
          )}
        </div>
      </div>

      {/* Stats bar */}
      <div className="tbw-stats">
        <span className="tbw-stat">
          <CheckCircle2 size={12} style={{ color: '#00C853' }} />
          {stats.done}/{stats.total} done
        </span>
        <span className="tbw-stat">
          <Loader size={12} style={{ color: '#FFB300' }} />
          {stats.inProgress} active
        </span>
        {stats.blocked > 0 && (
          <span className="tbw-stat">
            <Ban size={12} style={{ color: '#ff4444' }} />
            {stats.blocked} blocked
          </span>
        )}
        {stats.p0 > 0 && (
          <span className="tbw-stat tbw-stat-critical">
            🔴 {stats.p0} critical
          </span>
        )}
        {/* Progress bar */}
        <div className="tbw-progress">
          <div
            className="tbw-progress-fill"
            style={{ width: `${stats.total ? (stats.done / stats.total) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="tbw-filters">
        <div className="tbw-filter-row">
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="tbw-select">
            <option value="all">All Statuses</option>
            {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="tbw-select">
            <option value="all">All Priorities</option>
            {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}
          </select>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="tbw-select">
            <option value="all">All Categories</option>
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="tbw-select">
            <option value="priority">Sort: Priority</option>
            <option value="status">Sort: Status</option>
            <option value="created">Sort: Newest</option>
            <option value="updated">Sort: Updated</option>
          </select>
        </div>
        <div className="tbw-view-toggle">
          <button className={`tbw-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>
            List
          </button>
          <button className={`tbw-view-btn ${viewMode === 'board' ? 'active' : ''}`} onClick={() => setViewMode('board')}>
            Board
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreateForm && isAuthenticated && (
        <TaskForm
          onSave={async (task) => {
            const { data, error: err } = await supabase
              .from('tasks')
              .insert({ ...task, created_by: user.id })
              .select()
              .single()
            if (!err && data) {
              setTasks(prev => [data, ...prev])
              setShowCreateForm(false)
            }
          }}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* Error */}
      {error && (
        <div className="widget-body">
          <div className="widget-error">
            <AlertCircle size={14} /> {error}
            <button
              className="tbw-retry-btn"
              onClick={() => fetchTasks()}
              style={{ marginLeft: 8, padding: '2px 10px', fontSize: 12, cursor: 'pointer', background: '#1a3a5c', color: '#7ec8e3', border: '1px solid #234', borderRadius: 4 }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="widget-body">
          <div className="widget-loading"><Loader size={16} className="tbw-spinner" /> Loading tasks...</div>
        </div>
      )}

      {/* List view */}
      {!loading && !error && viewMode === 'list' && (
        <div className="tbw-list">
          {sorted.length === 0 && (
            <div className="widget-body"><div className="widget-empty">No tasks match filters</div></div>
          )}
          {sorted.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              isAuthenticated={isAuthenticated}
              onStatusChange={updateTaskStatus}
              onDelete={deleteTask}
              onEdit={setEditingTask}
            />
          ))}
        </div>
      )}

      {/* Board view */}
      {!loading && !error && viewMode === 'board' && (
        <div className="tbw-board">
          {STATUSES.map(status => {
            const statusTasks = grouped[status.id]
            const isCollapsed = collapsedStatuses.has(status.id)
            const Icon = status.icon
            return (
              <div key={status.id} className="tbw-column">
                <div
                  className="tbw-column-header"
                  onClick={() => toggleCollapse(status.id)}
                  style={{ borderLeftColor: status.color }}
                >
                  {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  <Icon size={14} style={{ color: status.color }} />
                  <span>{status.label}</span>
                  <span className="tbw-column-count">{statusTasks.length}</span>
                </div>
                {!isCollapsed && (
                  <div className="tbw-column-body">
                    {statusTasks.map(task => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        compact
                        isAuthenticated={isAuthenticated}
                        onStatusChange={updateTaskStatus}
                        onDelete={deleteTask}
                        onEdit={setEditingTask}
                      />
                    ))}
                    {statusTasks.length === 0 && (
                      <div className="tbw-empty-col">No tasks</div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Edit modal */}
      {editingTask && isAuthenticated && (
        <TaskForm
          task={editingTask}
          onSave={async (updates) => {
            const { error: err } = await supabase
              .from('tasks')
              .update({ ...updates, updated_at: new Date().toISOString() })
              .eq('id', editingTask.id)
            if (!err) {
              setTasks(prev => prev.map(t =>
                t.id === editingTask.id ? { ...t, ...updates, updated_at: new Date().toISOString() } : t
              ))
              setEditingTask(null)
            }
          }}
          onCancel={() => setEditingTask(null)}
          isEdit
        />
      )}
    </div>
  )
}

// --- Task Card ---
function TaskCard({ task, compact, isAuthenticated, onStatusChange, onDelete, onEdit }) {
  const [expanded, setExpanded] = useState(false)
  const priority = PRIORITY_MAP[task.priority] || PRIORITY_MAP.p2
  const category = CATEGORY_MAP[task.category] || CATEGORY_MAP.general
  const status = STATUS_MAP[task.status] || STATUS_MAP.backlog

  const age = Math.floor((Date.now() - new Date(task.created_at).getTime()) / 86400000)

  return (
    <div className={`tbw-card ${compact ? 'tbw-card-compact' : ''}`} style={{ borderLeftColor: priority.color }}>
      <div className="tbw-card-header" onClick={() => !compact && setExpanded(!expanded)}>
        <div className="tbw-card-meta">
          <span className="tbw-priority-dot" style={{ background: priority.color }} title={priority.label}>
            {priority.emoji}
          </span>
          <span className="tbw-category-badge" style={{ background: category.color + '22', color: category.color, borderColor: category.color + '44' }}>
            {category.label}
          </span>
        </div>
        <h4 className="tbw-card-title">{task.title}</h4>
        {!compact && (
          <div className="tbw-card-info">
            <span className="tbw-age">{age}d ago</span>
            <span className="tbw-status-badge" style={{ background: status.color + '22', color: status.color }}>
              {status.label}
            </span>
          </div>
        )}
      </div>

      {expanded && !compact && (
        <div className="tbw-card-body">
          {task.description && <p className="tbw-desc">{task.description}</p>}
          {isAuthenticated && (
            <div className="tbw-card-actions">
              <select
                value={task.status}
                onChange={(e) => onStatusChange(task.id, e.target.value)}
                className="tbw-status-select"
              >
                {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button className="tbw-edit-btn" onClick={() => onEdit(task)} title="Edit">
                <Edit3 size={12} />
              </button>
              <button className="tbw-delete-btn" onClick={() => { if (confirm('Delete this task?')) onDelete(task.id) }} title="Delete">
                <Trash2 size={12} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// --- Task Form (create/edit) ---
function TaskForm({ task, onSave, onCancel, isEdit }) {
  const [title, setTitle] = useState(task?.title || '')
  const [description, setDescription] = useState(task?.description || '')
  const [priority, setPriority] = useState(task?.priority || 'p2')
  const [category, setCategory] = useState(task?.category || 'general')
  const [status, setStatus] = useState(task?.status || 'backlog')

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!title.trim()) return
    onSave({ title: title.trim(), description: description.trim(), priority, category, status })
  }

  return (
    <div className="tbw-form-overlay" onClick={isEdit ? onCancel : undefined}>
      <form className={`tbw-form ${isEdit ? 'tbw-form-modal' : ''}`} onSubmit={handleSubmit} onClick={e => e.stopPropagation()}>
        <div className="tbw-form-header">
          <h4>{isEdit ? 'Edit Task' : 'New Task'}</h4>
          <button type="button" className="tbw-form-close" onClick={onCancel}><X size={16} /></button>
        </div>

        <input
          className="tbw-form-input"
          placeholder="Task title..."
          value={title}
          onChange={e => setTitle(e.target.value)}
          required
          autoFocus
        />

        <textarea
          className="tbw-form-textarea"
          placeholder="Description (optional)..."
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={3}
        />

        <div className="tbw-form-row">
          <select value={priority} onChange={e => setPriority(e.target.value)} className="tbw-select">
            {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}
          </select>
          <select value={category} onChange={e => setCategory(e.target.value)} className="tbw-select">
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          {isEdit && (
            <select value={status} onChange={e => setStatus(e.target.value)} className="tbw-select">
              {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          )}
        </div>

        <div className="tbw-form-actions">
          <button type="button" className="tbw-cancel-btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="tbw-save-btn">
            <Save size={14} /> {isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  )
}
